/**
 * 21/10/2026 — the engine settings' defaults, their tolerant reader and the
 * check a write must pass. Pure; the backend and the admin editor share it.
 * For Media Devoted | Created By Ciro Autiero
 */
import {
  DROP_GEN_ENGINE_SETTINGS,
  type DropGenEngineSettingDef,
  type DropGenEngineSettingKey,
  type DropGenEngineSettings,
} from "./engine-settings.defs";

export const DROP_GEN_ENGINE_DEFAULTS = Object.fromEntries(
  DROP_GEN_ENGINE_SETTINGS.map((d) => [d.key, d.default]),
) as DropGenEngineSettings;

const DEFS = new Map<string, DropGenEngineSettingDef>(DROP_GEN_ENGINE_SETTINGS.map((d) => [d.key, d]));

export const engineSettingDef = (key: string): DropGenEngineSettingDef | undefined => DEFS.get(key);

/** Why this value cannot be saved; null = it can. */
export function engineSettingProblem(key: string, value: unknown): string | null {
  const def = DEFS.get(key);
  if (!def) return `${key} is not an engine setting`;
  if (typeof value !== "number" || !Number.isFinite(value)) return `${def.label}: a number is needed`;
  if (def.choices && !def.choices.includes(value)) return `${def.label}: one of ${def.choices.join(", ")} ${def.unit}`;
  if (value < def.min || value > def.max) return `${def.label}: between ${def.min} and ${def.max} ${def.unit}`;
  if (!Number.isInteger(value)) return `${def.label}: a whole number`;
  return null;
}

/** A stored value made safe: a bad or missing one is the default. */
const safe = (def: DropGenEngineSettingDef, value: unknown): number =>
  engineSettingProblem(def.key, value) === null ? (value as number) : def.default;

/** Whatever was stored, read as a complete, valid set. */
export function engineSettingsOf(raw: unknown): DropGenEngineSettings {
  const stored = raw && typeof raw === "object" && !Array.isArray(raw) ? (raw as Record<string, unknown>) : {};
  return Object.fromEntries(DROP_GEN_ENGINE_SETTINGS.map((d) => [d.key, safe(d, stored[d.key])])) as DropGenEngineSettings;
}

export type { DropGenEngineSettingKey, DropGenEngineSettings };
